import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Actions, Store } from '@ngxs/store';
import { ModalService } from '@platform-frontend/ui-tools';
import { ActionClass, ClearNotifications, Navigate, SimpleRouterNavigationExtras, SimpleRouterState, filterDispatchedAction } from '@platform-frontend/util';
import { Buffer } from 'buffer';
import { EMPTY, Observable, merge } from 'rxjs';
import { map, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { AccountDisabledServiceToken, AuthenticationServiceToken, IAccountDisabledService, IAuthenticationService } from '../account-services';
import { GotoAcceptNewTerms, GotoDeactivatedAccount, GotoReactivateAccount } from '../disabled-details';
import { GotoRegistrationComplete, GotoVerifyEmail } from '../email-authentication';
import { GotoResetPassword, GotoResetPasswordDone, GotoSetPassword, GotoSetPasswordDone } from '../password-update';
import { GotoCheckRecoveryEmail, GotoSignIn } from '../session';
import { GotoNewTerms } from '../updated-terms';
import { AccountRouteService } from './account-route.service';
import { AccountGotoAction } from './account-router.actions';
import { AccountNavigationExtras } from './model/account-navigation-extras';
import { AccountStartParams } from './model/account-start-params';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class AccountNavigationService {
  constructor(
    @Inject(AccountDisabledServiceToken) private accountDisabledService: IAccountDisabledService,
    private accountRouteService: AccountRouteService,
    private actions$: Actions,
    @Inject(AuthenticationServiceToken) private authenticationService: IAuthenticationService,
    private modalService: ModalService,
    private router: Router,
    private store: Store
  ) {}

  init(): void {
    const accountDisabledParent = this.accountDisabledService.getRouteParent();
    const authenticationParent = this.authenticationService.getRouteParent();

    merge(
      this.handleGotoSignIn(authenticationParent, 'sign-in'),
      this.handleGotoAction(GotoVerifyEmail, authenticationParent, 'verify-email'),
      this.handleGotoAction(GotoAcceptNewTerms, accountDisabledParent, 'accept-new-terms'),
      this.handleGotoAction(GotoCheckRecoveryEmail, authenticationParent, 'check-recovery-email'),
      this.handleGotoAction(GotoNewTerms, authenticationParent, 'new-terms'),
      this.handleGotoAction(GotoSetPassword, authenticationParent, 'set-password'),
      this.handleGotoAction(GotoSetPasswordDone, authenticationParent, 'set-password-done'),
      this.handleGotoAction(GotoResetPassword, authenticationParent, 'reset-password'),
      this.handleGotoAction(GotoResetPasswordDone, authenticationParent, 'reset-password-done'),
      this.handleGotoAction(GotoRegistrationComplete, authenticationParent, 'registration-complete')
    )
      .pipe(untilDestroyed(this))
      .subscribe();

    if (this.accountDisabledService.gotoDeactivatedAccountRoutes()) {
      merge(
        this.handleGotoAction(GotoReactivateAccount, accountDisabledParent, 'reactivate-account'),
        this.handleGotoAction(GotoDeactivatedAccount, accountDisabledParent, 'deactivated-account')
      )
        .pipe(untilDestroyed(this))
        .subscribe();
    }
  }

  private handleGotoSignIn(routeParent: string, route: string): Observable<unknown> {
    return this.actions$.pipe(
      filterDispatchedAction([GotoSignIn]),
      map(signInAction => ({
        ...signInAction.extras,
        queryParams: { signInMode: signInAction.extras?.signInMode, ...signInAction.extras?.queryParams }
      })),
      this.handleGotoActionAccordingToApplicationState(routeParent, route)
    );
  }

  private handleGotoAction(action: ActionClass<AccountGotoAction>, routeParent: string, route: string): Observable<unknown> {
    return this.actions$.pipe(
      filterDispatchedAction([action]),
      map(dispatchedAction => dispatchedAction.extras),
      this.handleGotoActionAccordingToApplicationState(routeParent, route)
    );
  }

  private handleGotoActionAccordingToApplicationState =
    (routeParent: string, route: string) =>
    (obs: Observable<AccountNavigationExtras | undefined>): Observable<unknown> =>
      obs.pipe(
        withLatestFrom(this.authenticationService.isApplicationVisible()),
        switchMap(([extras, isApplicationVisible]) =>
          isApplicationVisible
            ? this.handleGotoActionIfApplicationVisible(routeParent, route, extras)
            : this.handleGotoActionIfApplicationHidden(extras?.clearNotifications)
        )
      );

  private handleGotoActionIfApplicationVisible(routeParent: string, route: string, extras?: AccountNavigationExtras): Observable<unknown> {
    return this.authenticationService.getGotoActionReplaceUrlDefault().pipe(
      take(1),
      switchMap(replaceUrlDefault =>
        this.store.dispatch(new Navigate([this.buildRouteWithParams(routeParent, route, extras)], this.buildNavigationExtras(replaceUrlDefault, extras)))
      ),
      switchMap(() => this.modalService.closeCurrent({ clearNotifications: extras?.clearNotifications }))
    );
  }

  private buildNavigationExtras(replaceUrlDefault: boolean, extras?: AccountNavigationExtras): SimpleRouterNavigationExtras {
    const replaceUrl: boolean | undefined = replaceUrlDefault && extras?.replaceUrl === undefined ? true : extras?.replaceUrl;
    return { ...extras, replaceUrl };
  }

  private buildRouteWithParams(routeParent: string, route: string, extras?: AccountNavigationExtras): string {
    const extendedExtras: AccountNavigationExtras = this.accountRouteService.extendAccountNavigationExtras(
      this.router.url,
      this.store.selectSnapshot(SimpleRouterState.paramMap),
      this.store.selectSnapshot(SimpleRouterState.queryParamMap),
      extras ?? {}
    );
    const startParams: AccountStartParams = {
      redirect: extendedExtras?.redirect,
      appStartParams: extendedExtras?.appStartParams
    };
    const base64StartParams = Buffer.from(JSON.stringify(startParams)).toString('base64');
    return routeParent + '/' + base64StartParams + '/' + route;
  }

  private handleGotoActionIfApplicationHidden(clearNotifications?: boolean): Observable<unknown> {
    return clearNotifications !== false ? this.store.dispatch(new ClearNotifications()) : EMPTY;
  }
}
